import { useEffect, useState } from 'react';
import {
  TerminalSquare, Folder, Clipboard, MessageSquarePlus, Settings,
  Globe, PlaySquare, Calculator, Bomb, FileCode2, Search, Power, ChevronRight,
} from 'lucide-react';
import { useStore } from '../../store/useStore';
import type { AppType } from '../../types';

interface PinnedApp {
  appType: AppType;
  label: string;
  icon: React.ReactNode;
  color: string;
}

const PINNED: PinnedApp[] = [
  { appType: 'terminal', label: 'Terminal', icon: <TerminalSquare className="w-5 h-5" />, color: 'from-cyan-500 to-blue-600' },
  { appType: 'file-manager', label: 'Files', icon: <Folder className="w-5 h-5" />, color: 'from-amber-500 to-orange-600' },
  { appType: 'projects', label: 'Projects', icon: <Clipboard className="w-5 h-5" />, color: 'from-purple-500 to-indigo-600' },
  { appType: 'editor', label: 'Code Editor', icon: <FileCode2 className="w-5 h-5" />, color: 'from-emerald-500 to-teal-600' },
  { appType: 'browser', label: 'Browser', icon: <Globe className="w-5 h-5" />, color: 'from-pink-500 to-rose-600' },
  { appType: 'chat', label: 'AI Assistant', icon: <MessageSquarePlus className="w-5 h-5" />, color: 'from-blue-500 to-indigo-600' },
  { appType: 'calculator', label: 'Calculator', icon: <Calculator className="w-5 h-5" />, color: 'from-orange-500 to-amber-600' },
  { appType: 'minesweeper', label: 'Minesweeper', icon: <Bomb className="w-5 h-5" />, color: 'from-red-500 to-rose-600' },
  { appType: 'preview', label: 'Live Preview', icon: <PlaySquare className="w-5 h-5" />, color: 'from-green-500 to-emerald-600' },
  { appType: 'settings', label: 'Settings', icon: <Settings className="w-5 h-5" />, color: 'from-slate-500 to-zinc-600' },
];

export function AppLauncher() {
  const open = useStore((s) => s.appLauncherOpen);
  const toggle = useStore((s) => s.toggleAppLauncher);
  const openApp = useStore((s) => s.openApp);
  const logout = useStore((s) => s.logout);
  const userEmail = useStore((s) => s.userEmail);
  const projects = useStore((s) => s.projects);
  const loadProjects = useStore((s) => s.loadProjects);
  const openProject = useStore((s) => s.openProject);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (open) {
      setQuery('');
      loadProjects().catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') toggle();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, toggle]);

  if (!open) return null;

  const q = query.trim().toLowerCase();
  const filtered = q ? PINNED.filter((a) => a.label.toLowerCase().includes(q)) : PINNED;
  const recent = [...projects]
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 4);

  const launch = (appType: AppType) => {
    openApp(appType);
    toggle();
  };

  return (
    <>
      <div className="fixed inset-0 z-[4900]" onClick={toggle} />
      <div className="fixed left-1/2 -translate-x-1/2 bottom-14 z-[4950] w-[620px] max-w-[calc(100vw-2rem)]">
        <div className="glass rounded-2xl shadow-2xl overflow-hidden border border-desktop-border animate-window-open">
          {/* Search */}
          <div className="px-8 pt-6">
            <div className="flex items-center gap-2 px-3 h-9 rounded-full bg-desktop-surface2 border border-desktop-border focus-within:border-desktop-accent transition-colors">
              <Search className="w-3.5 h-3.5 text-desktop-text/50 shrink-0" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type here to search"
                className="flex-1 bg-transparent text-xs text-desktop-text placeholder:text-desktop-text/40 focus:outline-none"
              />
            </div>
          </div>

          {/* Pinned */}
          <div className="px-8 pt-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-desktop-text">Pinned</h3>
            </div>
            {filtered.length === 0 ? (
              <p className="text-xs text-desktop-text/50 py-4 text-center">No apps match "{query}"</p>
            ) : (
              <div className="grid grid-cols-6 gap-y-4">
                {filtered.map((app) => (
                  <button
                    key={app.appType}
                    onClick={() => launch(app.appType)}
                    className="group flex flex-col items-center gap-1.5 py-1.5 rounded-lg hover:bg-desktop-hover/60 transition-colors"
                  >
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${app.color} flex items-center justify-center text-white shadow group-hover:scale-105 transition-transform`}>
                      {app.icon}
                    </div>
                    <span className="text-[11px] text-desktop-text text-center leading-tight px-1 truncate w-full">{app.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Recommended */}
          <div className="px-8 pt-5 pb-2">
            <h3 className="text-xs font-semibold text-desktop-text mb-2">Recommended</h3>
            {recent.length === 0 ? (
              <p className="text-[11px] text-desktop-text/50 py-2">Open a project to see it here.</p>
            ) : (
              <div className="grid grid-cols-2 gap-1">
                {recent.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => { openProject(p).catch(() => {}); toggle(); }}
                    className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-desktop-hover/60 transition-colors text-left"
                  >
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white shrink-0">
                      <Folder className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-desktop-text truncate">{p.name}</p>
                      <p className="text-[10px] text-desktop-text/50">Recently opened</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mt-3 px-8 h-14 flex items-center justify-between bg-desktop-surface/60 border-t border-desktop-border">
            <button
              onClick={() => launch('settings')}
              className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-desktop-hover/60 transition-colors min-w-0"
              title="Account settings"
            >
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-desktop-accent to-purple-600 flex items-center justify-center text-white text-xs font-semibold shrink-0">
                {(userEmail?.[0] || 'U').toUpperCase()}
              </div>
              <span className="text-xs text-desktop-text truncate max-w-44">{userEmail || 'User'}</span>
            </button>
            <div className="flex items-center gap-1">
              <button
                onClick={() => launch('settings')}
                className="h-8 px-3 flex items-center gap-1.5 rounded-lg hover:bg-desktop-hover/60 text-desktop-text/80 text-xs transition-colors"
              >
                <Settings className="w-3.5 h-3.5" />
                <ChevronRight className="w-3 h-3 opacity-50" />
              </button>
              <button
                onClick={() => { logout().catch(() => {}); toggle(); }}
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-desktop-hover/60 text-desktop-text/80 hover:text-desktop-text transition-colors"
                title="Shut down (log out)"
              >
                <Power className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
